// Iter47-A — Admin → Service Request Types (master data CRUD).
// Replaces the iter46-B per-tenant config page (still available at the
// /api/admin/sr-type-config endpoint for future per-tenant overrides).
import React, { useState } from "react";
import { Wrench } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { useT } from "../lib/i18n";
import {
  useSrTypeMaster,
  useCreateSrType,
  useUpdateSrType,
  useDeleteSrType,
} from "../api/sitroom";
import {
  mono, inputStyle, Label, NameRow, SystemBadge, RowActions, NewRowButton, SaveCancelButtons, pickName,
} from "../components/MasterDataAtoms";

const MODULES = ["sorting", "tms", "generic"];
const ASSIGNEE_CATEGORIES = ["QualityTeam", "LogisticsCoord", "EngineeringLead", "SortingPartner", "Carrier", "PlantManager"];

export default function AdminSrTypes() {
  const { user } = useAuth();
  const { locale } = useT();
  const addToast = useStore((s) => s.addToast);
  const { data: rows = [], isLoading } = useSrTypeMaster();
  const createMut = useCreateSrType();
  const updateMut = useUpdateSrType();
  const deleteMut = useDeleteSrType();
  const isPlatform = user?.role === "platform_admin";

  const [editing, setEditing] = useState(null); // null|"new"|<key>

  const onSave = async (key, body, isNew) => {
    try {
      if (isNew) {
        await createMut.mutateAsync(body);
        addToast({ type: "success", title: "SR type created", body: body.names?.en });
      } else {
        await updateMut.mutateAsync({ key, body });
        addToast({ type: "success", title: "SR type updated", body: body.names?.en || key });
      }
      setEditing(null);
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete SR type "${pickName(row, locale)}"?`)) return;
    try {
      await deleteMut.mutateAsync(row.key);
      addToast({ type: "success", title: "SR type deleted" });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-sr-types">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Service Request Types · {rows.length}
        </div>
        {isPlatform && <NewRowButton onClick={() => setEditing("new")} label="New SR type" testId="sr-type-new-btn" />}
      </div>

      {isLoading && <div style={mono(11, "var(--dimmer, #9198b0)")}>Loading…</div>}

      <div className="space-y-2">
        {rows.map((row) => (
          <Row
            key={row.key} row={row} isPlatform={isPlatform} locale={locale}
            editing={editing === row.key}
            onEdit={() => setEditing(row.key)}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(row.key, body, false)}
            onDelete={() => onDelete(row)}
          />
        ))}
        {editing === "new" && (
          <Row
            row={null} isPlatform={isPlatform} locale={locale}
            editing={true}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(null, body, true)}
          />
        )}
      </div>
    </div>
  );
}

const Row = ({ row, isPlatform, locale, editing, onEdit, onCancel, onSave, onDelete }) => {
  const [key, setKey] = useState(row?.key || "");
  const [names, setNames] = useState(row?.names || { en: "", de: "", hu: "" });
  const [module, setModule] = useState(row?.module || "generic");
  const [assignee, setAssignee] = useState(row?.default_assignee_category || "");
  const [icon, setIcon] = useState(row?.icon || "Wrench");

  const handleSave = () => {
    if (!names.en?.trim()) return;
    const body = {
      names: { en: names.en?.trim(), de: names.de?.trim() || names.en?.trim(), hu: names.hu?.trim() || names.en?.trim() },
      module,
      icon: icon || "Wrench",
      default_assignee_category: assignee || null,
    };
    if (!row) body.key = key.trim();
    onSave(body);
  };

  if (editing) {
    return (
      <div className="rounded-md p-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }}
        data-testid={row ? `sr-type-edit-${row.key}` : "sr-type-edit-new"}>
        {!row && (
          <div className="mb-3">
            <Label>Key (lowercase, alpha-numeric)</Label>
            <input value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
              placeholder="e.g. custom_audit" style={inputStyle} data-testid="sr-type-edit-key" />
          </div>
        )}
        <NameRow names={names} onChange={setNames} testIdPrefix="sr-type-edit" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <div>
            <Label>Module</Label>
            <select value={module} onChange={(e) => setModule(e.target.value)} style={inputStyle} data-testid="sr-type-edit-module">
              {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label>Default assignee category</Label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} style={inputStyle} data-testid="sr-type-edit-assignee">
              <option value="">— none —</option>
              {ASSIGNEE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Icon (lucide-react)</Label>
            <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Wrench" style={inputStyle} data-testid="sr-type-edit-icon" />
          </div>
        </div>
        <SaveCancelButtons onSave={handleSave} onCancel={onCancel} testIdPrefix="sr-type-edit" />
      </div>
    );
  }

  return (
    <div
      className="rounded-md p-3"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: `1px solid ${row.system ? "rgba(243,177,74,0.25)" : "rgba(255,255,255,0.10)"}`,
      }}
      data-testid={`sr-type-row-${row.key}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Wrench size={14} color="#9ec3ec" />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
            {pickName(row, locale)}
          </span>
          <span style={mono(10, "var(--dimmer, #9198b0)")}>key={row.key}</span>
          <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(24,95,165,0.18)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px") }}>{row.module}</span>
          {row.default_assignee_category && <span style={mono(10, "var(--text-dim, #c5cad8)")}>→ {row.default_assignee_category}</span>}
          {row.system && <SystemBadge />}
        </div>
        {isPlatform && (
          <RowActions row={row} onEdit={onEdit} onDelete={onDelete}
            canDelete={!row.system}
            testIdPrefix="sr-type" />
        )}
      </div>
    </div>
  );
};
